import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AgentCard from './components/AgentCard';
import AgentInterface from './components/AgentInterface';
import ProgressIndicator from './components/ProgressIndicator';
import QuickActions from './components/QuickActions';

const AIAgentWorkflowHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [showAgentList, setShowAgentList] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      if (!isAuthenticated) {
        navigate('/user-login');
      }
    };

    checkAuth();

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  useEffect(() => {
    const agentWorkflow = [
      {
        id: 1,
        name: 'Topic Selection & Research',
        description: 'Analyze Amazon BSR data and generate 20 high-potential book topics',
        icon: 'Target',
        status: 'in-progress'
      },
      {
        id: 2,
        name: 'Writing Voice Capture',
        description: 'Upload PDF samples to analyze and capture your unique writing style',
        icon: 'Mic',
        status: 'locked'
      },
      {
        id: 3,
        name: 'Outline Creation',
        description: 'Generate detailed book outline with table of contents',
        icon: 'List',
        status: 'locked'
      },
      {
        id: 4,
        name: 'Writing Path Selection',
        description: 'Choose between automatic generation or chapter-by-chapter writing',
        icon: 'GitBranch',
        status: 'locked'
      },
      {
        id: 5,
        name: 'Automatic Manuscript',
        description: 'Full automatic manuscript generation in one session',
        icon: 'Zap',
        status: 'locked'
      },
      {
        id: 6,
        name: 'Chapter-by-Chapter Writing',
        description: 'Interactive chapter writing with user input and refinement',
        icon: 'Edit',
        status: 'locked'
      },
      {
        id: 7,
        name: 'Print Formatting',
        description: 'Format manuscript for Amazon KDP print-on-demand',
        icon: 'BookOpen',
        status: 'locked'
      },
      {
        id: 8,
        name: 'Ebook Formatting',
        description: 'Optimize manuscript for Kindle and ebook platforms',
        icon: 'Smartphone',
        status: 'locked'
      },
      {
        id: 9,
        name: 'Cover Design',
        description: 'Create professional book cover with competitor research',
        icon: 'Image',
        status: 'locked'
      },
      {
        id: 10,
        name: 'KDP Metadata',
        description: 'Generate optimized categories, keywords, and descriptions',
        icon: 'Tag',
        status: 'locked'
      },
      {
        id: 11,
        name: 'Publishing Guidance',
        description: 'Step-by-step instructions for Amazon KDP publishing',
        icon: 'Send',
        status: 'locked'
      }
    ];

    setAgents(agentWorkflow);
    setCurrentAgent(agentWorkflow?.[0]);
  }, []);

  const handleAgentSelect = (agent) => {
    if (agent?.status !== 'locked') {
      setCurrentAgent(agent);
      if (isMobile) {
        setShowAgentList(false);
      }
    }
  };

  const handleAgentComplete = () => {
    const currentIndex = agents?.findIndex(a => a?.id === currentAgent?.id);
    
    const updatedAgents = agents?.map((agent, index) => {
      if (index === currentIndex) {
        return { ...agent, status: 'completed' };
      }
      if (index === currentIndex + 1) {
        return { ...agent, status: 'in-progress' };
      }
      return agent;
    });

    setAgents(updatedAgents);

    if (currentIndex < agents?.length - 1) {
      setCurrentAgent(updatedAgents?.[currentIndex + 1]);
    }
  };

  const handleNext = () => {
    const currentIndex = agents?.findIndex(a => a?.id === currentAgent?.id);
    if (currentIndex < agents?.length - 1) {
      const nextAgent = agents?.[currentIndex + 1];
      if (nextAgent?.status !== 'locked') {
        setCurrentAgent(nextAgent);
      }
    }
  };

  const handleBack = () => {
    const currentIndex = agents?.findIndex(a => a?.id === currentAgent?.id);
    if (currentIndex > 0) {
      setCurrentAgent(agents?.[currentIndex - 1]);
    }
  };

  const handleSaveProgress = () => {
    localStorage.setItem('workflowProgress', JSON.stringify({
      agents,
      currentAgentId: currentAgent?.id,
      timestamp: new Date()?.toISOString()
    }));
    
    alert('Progress saved successfully!');
  };

  const handleExportContent = () => {
    alert('Export functionality will download all generated content as a ZIP file.');
  };

  const handleBackToList = () => {
    setShowAgentList(true);
  };

  if (!currentAgent) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader" size={48} className="mx-auto mb-4 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Loading workflow...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  AI Agent Workflow Hub
                </h1>
                <p className="text-sm text-muted-foreground">
                  Complete your book creation journey with 11 specialized AI agents
                </p>
              </div>
              {isMobile && !showAgentList && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="List"
                  iconPosition="left"
                  onClick={handleBackToList}
                >
                  Agents
                </Button>
              )}
            </div>

            <ProgressIndicator agents={agents} currentAgentId={currentAgent?.id} />
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {(showAgentList || !isMobile) && (
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Workflow Agents
                  </h2>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {agents?.map((agent) => (
                      <AgentCard
                        key={agent?.id}
                        agent={agent}
                        isActive={currentAgent?.id === agent?.id}
                        isCompleted={agent?.status === 'completed'}
                        isLocked={agent?.status === 'locked'}
                        onClick={() => handleAgentSelect(agent)}
                      />
                    ))}
                  </div>
                </div>

                {!isMobile && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <QuickActions
                      onSaveProgress={handleSaveProgress}
                      onExportContent={handleExportContent}
                    />
                  </div>
                )}
              </div>
            )}

            {(!showAgentList || !isMobile) && (
              <div className="lg:col-span-8">
                <div className="bg-card border border-border rounded-lg p-6">
                  <AgentInterface
                    agent={currentAgent}
                    onComplete={handleAgentComplete}
                    onBack={handleBack}
                    onNext={handleNext}
                    isFirst={currentAgent?.id === 1}
                    isLast={currentAgent?.id === agents?.length}
                  />
                </div>

                {isMobile && (
                  <div className="mt-4 bg-card border border-border rounded-lg p-4">
                    <QuickActions
                      onSaveProgress={handleSaveProgress}
                      onExportContent={handleExportContent}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="flex-shrink-0 mt-0.5 text-primary" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Workflow Tips
                </h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Progress is automatically saved after each agent completion</li>
                  <li>• You can return to previous agents to make changes</li>
                  <li>• All AI-generated content maintains your unique writing voice</li>
                  <li>• Export your content at any stage for external editing</li>
                  <li>• Join the Skool community for publishing support and marketing guidance</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIAgentWorkflowHub;